import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.pexels.com/photos/31622917/pexels-photo-31622917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO_BG =
  "https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkYXJrJTIwYWJzdHJhY3QlMjBnb2xkfGVufDB8fHx8MTc5MDE1OTEyNXww&ixlib=rb-4.1.0&q=85";

const LINES = ["INVEST WHERE", "CAPITAL MEETS", "CRAFT."];

export const Hero = ({ onExplore, onJoin }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-16 items-center pt-32 pb-24">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 1 }}
            className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-10"
            data-testid="hero-eyebrow"
          >
            MetrixWorks Service Pvt Ltd — Wealth, Engineered
          </motion.p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-white">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`hero-line-${i}`}
                >
                  {i === 2 ? <em className="text-[#D4AF37]">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 max-w-md text-white/60 text-base leading-relaxed"
          >
            Finance tools and curated investment plans across cinema, sovereign gold and the currency
            markets — built for investors who expect more.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-5"
          >
            <button
              data-testid="hero-explore-plans-button"
              onClick={onExplore}
              className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
            >
              Explore Plans
            </button>
            <button
              data-testid="hero-become-investor-button"
              onClick={onJoin}
              className="border border-white/20 text-white/80 px-8 py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:border-white/60 hover:text-white transition-colors"
            >
              Become an Investor
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImage }} className="lg:col-span-5 hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full border border-[#D4AF37]/30" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO_IMG} alt="Gold abstract sculpture" className="w-full h-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
            </div>
            <div className="absolute bottom-6 -left-10 bg-[#0A0A0A]/90 backdrop-blur-md border border-white/10 px-6 py-4">
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/40">Across</p>
              <p className="font-display text-2xl text-[#D4AF37]">6 Investment Plans</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
